import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { formatDate, parsePgDateTime } from '@/shared/utils/datetime';
import { mapDeviceActionTypeToFuente } from '@/modules/attendance/domain/helpers/deviceActionType';
import { resolveVerificationLabels } from '@/modules/attendance/domain/helpers/verificationMode';
import { useReverseGeocode } from '@/modules/employees/presentation/hooks/useReverseGeocode';
import { GoogleMapEmbed } from '@/modules/employees/presentation/components/GoogleMapEmbed';
import { Avatar } from '@/presentation/components/Avatar';
import { VerificationIcon } from './VerificationIcon';
import { useTimelogSyncStatus } from '@/shared/sync/useTimelogSyncStatus';

// Sync-status pill design tokens — locked per spec, MUST NOT vary by theme.
// `'failed'` is rendered with the same visual as `'pending'` (per spec scenario).
const SYNC_PILL_RED  = '#dc2626';
const SYNC_PILL_TEAL = '#14b8a6';

// GoogleMapEmbed is wrapped in React.memo at the call site below to avoid
// re-mounting the iframe when only the sync-pill re-renders.
const MemoGoogleMapEmbed = memo(GoogleMapEmbed);

// ── InfoRow ────────────────────────────────────────────────────────────────
// Slice C2: icon color locked to platform blue (C.blue); value fontSize 14.
interface InfoRowProps { icon: ReactNode; label: string; C: BrandColors; T: ThemeTokens }
function InfoRow({ icon, label, C, T }: InfoRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: T.text }}>
      <span
        data-testid="timelog-inforow-icon"
        style={{
          color: C.blue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span
        data-testid="timelog-inforow-value"
        style={{ color: T.text, fontWeight: 500, fontSize: 14, width: 110 }}
      >
        {label}
      </span>
    </div>
  );
}

// ── Pill ───────────────────────────────────────────────────────────────────
interface PillProps {
  icon: ReactNode;
  label: string;
  bg: string;
  color: string;
  truncate?: boolean;
  T: ThemeTokens;
  testId?: string;
  dataStatus?: string;
}
function Pill({ icon, label, bg, color, truncate, T, testId, dataStatus }: PillProps) {
  return (
    <div
      data-testid={testId}
      data-status={dataStatus}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        background: bg,
        borderRadius: 18,
        padding: '7px 12px',
        minHeight: 30,
      }}
    >
      <span style={{ color, display: 'flex', alignItems: 'center', flexShrink: 0, marginTop: 1 }}>
        {icon}
      </span>
      <span
        style={{
          fontSize: 14,
          color: T.text,
          fontWeight: 500,
          overflow: truncate ? 'hidden' : 'visible',
          textOverflow: truncate ? 'ellipsis' : 'clip',
          whiteSpace: truncate ? 'nowrap' : 'normal',
          wordBreak: truncate ? 'normal' : 'break-word',
          lineHeight: 1.4,
        }}
      >
        {label}
      </span>
    </div>
  );
}

interface Props {
  timelog: Timelog;
  initials: string;
  avatarColor: string;
  onClose: () => void;
  T: ThemeTokens;
  C: BrandColors;
}

export function TimelogDetailModal({ timelog, initials, avatarColor, onClose, T, C }: Props) {
  const { t } = useTranslation('common');
  const d = parsePgDateTime(timelog.datetime);
  const isEntrada   = timelog.accessType === 1;
  const accionColor = isEntrada ? C.success : C.danger;
  const accionLabel = isEntrada ? t('timelog.entryRegular') : t('timelog.exitRegular');
  const timeLabel   = d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true });

  // Resolve method + source labels via canonical lookup table.
  const labels = resolveVerificationLabels(timelog.verificationMode, timelog.source ?? null, t);

  // Source (Fuente) row: prefer the canonical resolver; fall back to legacy mapper.
  const hasVerificationMode = timelog.verificationMode != null;
  const fuenteLabel = hasVerificationMode
    ? labels.source
    : mapDeviceActionTypeToFuente(timelog.deviceActionType ?? undefined);

  // Coordinates from API can arrive as strings or numbers — coerce.
  const rawLat = timelog.coordinates?.lat;
  const rawLng = timelog.coordinates?.lng;
  const coordLat = rawLat != null ? Number(rawLat) : 0;
  const coordLng = rawLng != null ? Number(rawLng) : 0;
  const hasRealCoords =
    Number.isFinite(coordLat) &&
    Number.isFinite(coordLng) &&
    (coordLat !== 0 || coordLng !== 0);
  const { data: geocode } = useReverseGeocode(coordLat, coordLng);

  // Sync-status pill — reactive Dexie subscription when localId is present.
  // Carry-forward CF-3: pre-Slice-C1 entries may have syncStatus === undefined;
  // we treat that as 'synced' for display purposes (spec R-entity).
  const liveStatus = useTimelogSyncStatus(
    timelog.localId,
    timelog.syncStatus ?? 'synced',
  );
  const isSynced = liveStatus === 'synced';
  const syncPillColor = isSynced ? SYNC_PILL_TEAL : SYNC_PILL_RED;
  const syncPillLabel = isSynced
    ? t('timelog.status.synced')
    : t('timelog.status.pending');

  return (
    <div
      className="mi-anim-fade-in"
      style={{
        position: 'fixed',
        inset: 0,
        background: T.modalBg,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        className="mi-anim-modal-in"
        style={{
          background: T.surfaceBg,
          borderRadius: 14,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: T.shadow,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '11px 15px',
            borderBottom: `1px solid ${T.border}`,
            position: 'relative',
            minHeight: 48,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: T.clockText,
              pointerEvents: 'none',
            }}
          >
            {t('timelog.detailTitle')}
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `${C.danger}12`,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.danger,
              flexShrink: 0,
              transition: 'background .15s',
              position: 'relative',
              zIndex: 1,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${C.danger}22`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = `${C.danger}12`; }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 11 }}>
          {/* Details + pills grid (avatar | metadata | pills) */}
          <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr 1fr', gap: 14, alignItems: 'start' }}>
            {/* Avatar */}
            <Avatar
              initials={initials}
              color={avatarColor}
              size={100}
              letterFontSize={25.2}
              photoUrl={timelog.capturePhotoUrl ?? timelog.employee?.photoUrl ?? null}
            />

            {/* Left metadata (InfoRows — icons in C.blue, values at 14px) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingTop: 2 }}>
              <InfoRow
                T={T}
                C={C}
                icon={
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                }
                label={formatDate(d)}
              />
              <InfoRow
                T={T}
                C={C}
                icon={
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                }
                label={timeLabel}
              />
              <InfoRow
                T={T}
                C={C}
                icon={<VerificationIcon iconKey={labels.methodIconKey} size={14} />}
                label={labels.method}
              />
              <InfoRow
                T={T}
                C={C}
                icon={
                  hasVerificationMode ? (
                    <VerificationIcon iconKey={labels.sourceIconKey} size={14} />
                  ) : (
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  )
                }
                label={fuenteLabel}
              />
            </div>

            {/* Right pills (action + sync — verificationResult REMOVED) */}
            <div
              data-testid="timelog-pills-column"
              style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingTop: 2 }}
            >
              <Pill
                T={T}
                bg={`${accionColor}14`}
                color={accionColor}
                label={accionLabel}
                icon={
                  isEntrada ? (
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                  ) : (
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  )
                }
              />
              <Pill
                T={T}
                testId="timelog-sync-pill"
                dataStatus={isSynced ? 'synced' : 'pending'}
                bg={`${syncPillColor}14`}
                color={syncPillColor}
                label={syncPillLabel}
                icon={
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                }
              />
            </div>
          </div>

          {/* Full-width address row — placed BETWEEN details grid and map.
              Only renders when real coordinates exist. */}
          {timelog.coordinates && hasRealCoords && (
            <div
              data-testid="timelog-address-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                background: `${C.blue}10`,
                borderRadius: 9,
                padding: '8px 12px',
                color: C.blue,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
            >
              <span style={{ color: C.blue, display: 'flex', flexShrink: 0 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <span>
                {geocode?.formattedAddress ??
                  `${timelog.coordinates.lat.toFixed(4)}, ${timelog.coordinates.lng.toFixed(4)}`}
              </span>
            </div>
          )}

          {/* Notes — only show if there's a description/observation */}
          {timelog.description && (
            <div
              style={{
                background: T.rowBg,
                borderRadius: 9,
                padding: '8px 12px',
                fontSize: 14,
                color: T.textBody,
              }}
            >
              {timelog.description}
            </div>
          )}

          {/* Map (memoized to avoid remount when the sync pill re-renders) */}
          <MemoGoogleMapEmbed
            lat={coordLat}
            lng={coordLng}
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 18px 14px',
            borderTop: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 36px',
              borderRadius: 9,
              border: 'none',
              background: C.danger,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${C.danger}33`,
            }}
          >
            {t('timelog.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
